/**
 * 主题页面 —— 通用条目列表 + 原位展开详情。
 * 41 个通用主题共享此页；点击条目在当前位置展开详情卡片。
 * 展开状态同步到 URL hash（如 /topic/fishing.fish#catfish）。
 */

import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Heart } from 'lucide-react';
import { topic as getTopic, topics } from '@/core/moduleCatalog';
import type { ContentRepository } from '@/core/contentRepository';
import type { EncyclopediaEntry, EntryContentBlock, EntryReference } from '@/core/models';
import { EntryIcon } from '@/components/EntryIcon';
import { EntryReferenceChip } from '@/components/EntryReferenceChip';
import { useLibraryStore } from '@/store/useLibraryStore';
import { cn } from '@/lib/cn';
import { entryRowHighlights } from './entryRowHighlights';

interface TopicPageProps {
  repository: ContentRepository;
}

export function TopicPage({ repository }: TopicPageProps) {
  const { topicId } = useParams<{ topicId: string }>();
  const location = useLocation();
  const t = topicId ? getTopic(topicId) : undefined;

  const entries = useMemo(
    () => (topicId ? repository.entriesForTopic(topicId) : []),
    [repository, topicId],
  );

  // 从 URL hash 读取展开状态
  const expandedId = location.hash?.replace(/^#/, '') || null;

  if (!t) {
    return (
      <div className="py-20 text-center">
        <p className="text-lg font-semibold text-slate-400">未找到主题</p>
        <Link to="/" className="mt-2 inline-block text-sm text-sage hover:underline">
          返回首页
        </Link>
      </div>
    );
  }

  // 按拼音排序
  const sorted = [...entries].sort((a, b) =>
    a.nameZh.localeCompare(b.nameZh, 'zh-Hans'),
  );

  return (
    <div className="space-y-4">
      {/* 头部 */}
      <div className="flex items-center gap-3">
        <Link to={`/module/${t.moduleID}`} className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800">
          <ArrowLeft size={20} />
        </Link>
        <div>
          <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">{t.title}</h1>
          <p className="text-xs text-slate-500">
            {topics.find((tp) => tp.id === t.id)?.title ?? ''} · 共 {entries.length} 条
          </p>
        </div>
      </div>

      {/* 条目列表 */}
      <div className="space-y-1">
        {sorted.map((entry) => (
          <EntryRow
            key={entry.id}
            entry={entry}
            repository={repository}
            isExpanded={expandedId === entry.id}
          />
        ))}
      </div>

      {entries.length === 0 && (
        <div className="py-20 text-center text-sm text-slate-400">
          该主题暂无条目
        </div>
      )}
    </div>
  );
}

/* --------------------------- EntryRow --------------------------- */

interface EntryRowProps {
  entry: EncyclopediaEntry;
  repository: ContentRepository;
  isExpanded: boolean;
}

function EntryRow({ entry, repository, isExpanded }: EntryRowProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isFavorite = useLibraryStore((s) => s.isFavorite(entry.id));
  const toggleFavorite = useLibraryStore((s) => s.toggleFavorite);
  const recordVisit = useLibraryStore((s) => s.recordVisit);

  // 展开时滚动到视图
  useEffect(() => {
    if (isExpanded && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      recordVisit(entry.id);
    }
  }, [isExpanded, entry.id, recordVisit]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(entry.id);
  };

  const toggleUrl = isExpanded
    ? `#`
    : `#${entry.id}`;

  return (
    <div ref={ref}>
      {/* 标题行 — 点击展开/收起 */}
      <Link
        to={toggleUrl}
        replace
        className={cn(
          'flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors',
          isExpanded
            ? 'bg-sage/5 dark:bg-sage/10'
            : 'hover:bg-slate-100 dark:hover:bg-slate-800',
        )}
      >
        <EntryIcon entry={entry} size={36} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
            {entry.nameZh}
            {entry.nameEn && (
              <span className="ml-1.5 text-xs text-slate-400">{entry.nameEn}</span>
            )}
          </p>
          <EntryRowHighlights entry={entry} />
        </div>

        {/* 收藏按钮 */}
        <button
          onClick={handleToggle}
          className={cn(
            'shrink-0 transition-colors',
            isFavorite ? 'text-red-400' : 'text-slate-300 hover:text-red-300',
          )}
        >
          <Heart size={16} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      </Link>

      {/* 展开详情 */}
      {isExpanded && (
        <div className="mx-3 mb-2 overflow-hidden rounded-2xl border border-slate-200/60 bg-surface-light shadow-sm dark:border-slate-700/40 dark:bg-surface-dark">
          <EntryDetail entry={entry} repository={repository} />
        </div>
      )}
    </div>
  );
}

function EntryRowHighlights({ entry }: { entry: EncyclopediaEntry }) {
  const highlights = entryRowHighlights(entry);

  if (highlights.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-1">
      {highlights.map((highlight) => (
        <span
          key={highlight}
          className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] leading-none text-slate-500 dark:bg-slate-800 dark:text-slate-400"
        >
          {highlight}
        </span>
      ))}
    </div>
  );
}

/* ------------------------- EntryDetail ------------------------- */

interface EntryDetailProps {
  entry: EncyclopediaEntry;
  repository: ContentRepository;
}

function EntryDetail({ entry, repository }: EntryDetailProps) {
  const recordVisit = useLibraryStore((s) => s.recordVisit);

  useEffect(() => {
    recordVisit(entry.id);
  }, [entry.id, recordVisit]);

  return (
    <div className="space-y-4 p-4">
      {/* 摘要 */}
      {entry.summary && (
        <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {entry.summary}
        </p>
      )}

      {/* 速查字段 */}
      {entry.facts.length > 0 && (
        <div className="space-y-2">
          {entry.facts.map((fact, i) => (
            <div key={i} className="flex items-baseline gap-2">
              <span className="shrink-0 text-xs font-medium text-slate-500">
                {fact.label}
              </span>
              <span
                className={cn(
                  'text-sm',
                  fact.emphasis
                    ? 'font-semibold text-sage'
                    : 'text-slate-800 dark:text-slate-200',
                )}
              >
                {fact.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* 富内容块：用于条件、引用列表和表格等结构化攻略信息 */}
      {entry.contentBlocks && entry.contentBlocks.length > 0 && (
        <div className="space-y-3 border-t border-slate-200/60 pt-3 dark:border-slate-700/40">
          {entry.contentBlocks.map((block, i) => (
            <ContentBlockView key={`${block.type}-${block.title}-${i}`} block={block} repository={repository} />
          ))}
        </div>
      )}

      {/* 补充说明段落 */}
      {entry.sections.length > 0 && (
        <div className="space-y-3 border-t border-slate-200/60 pt-3 dark:border-slate-700/40">
          {entry.sections.map((section, i) => (
            <div key={i}>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                {section.title}
              </h4>
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                {section.body}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* 相关条目 */}
      {entry.relatedEntryIDs.length > 0 && (
        <div className="border-t border-slate-200/60 pt-3 dark:border-slate-700/40">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            相关条目
          </h4>
          <div className="flex flex-wrap gap-2">
            {entry.relatedEntryIDs.map((relId) => (
              <RelatedEntryLink key={relId} id={relId} repository={repository} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------ ContentBlockView ------------------------ */

interface ContentBlockViewProps {
  block: EntryContentBlock;
  repository: ContentRepository;
}

function ContentBlockView({ block, repository }: ContentBlockViewProps) {
  switch (block.type) {
    case 'condition-list':
      return (
        <section>
          <BlockTitle title={block.title} />
          <div className="flex flex-wrap gap-1.5">
            {block.conditions.map((condition) => (
              <span
                key={condition}
                className="rounded-full bg-sage/10 px-2.5 py-1 text-xs font-medium text-sage dark:bg-sage/20"
              >
                {condition}
              </span>
            ))}
          </div>
        </section>
      );

    case 'reference-list':
      return (
        <section>
          <BlockTitle title={block.title} />
          <div className="flex flex-wrap gap-2">
            {block.references.map((reference) => (
              <RichReference key={referenceKey(reference)} reference={reference} repository={repository} />
            ))}
          </div>
        </section>
      );

    case 'data-table':
      return (
        <section>
          <BlockTitle title={block.title} />
          <div className="overflow-x-auto rounded-xl border border-slate-200/70 dark:border-slate-700/50">
            <table className="min-w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 dark:bg-slate-800/70 dark:text-slate-400">
                <tr>
                  {block.columns.map((column) => (
                    <th key={column} scope="col" className="px-3 py-2 font-semibold">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/70 dark:divide-slate-700/50">
                {block.rows.map((row, rowIndex) => (
                  <tr key={`${block.title}-${rowIndex}`}>
                    {row.map((cell, cellIndex) => (
                      <td key={`${block.title}-${rowIndex}-${cellIndex}`} className="px-3 py-2 text-slate-700 dark:text-slate-300">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );

    case 'timeline':
    case 'callout':
      return null;
  }
}

function BlockTitle({ title }: { title: string }) {
  return (
    <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
      {title}
    </h4>
  );
}

function RichReference({
  reference,
  repository,
}: {
  reference: EntryReference;
  repository: ContentRepository;
}) {
  const entry = repository.entry(reference.entryID);
  const to = entry ? `/topic/${entry.topicID}#${entry.id}` : '#';
  const label = reference.label ?? entry?.nameZh ?? reference.entryID;

  return (
    <div className="inline-flex flex-col gap-1">
      <EntryReferenceChip
        entry={entry}
        fallbackID={label}
        to={to}
        className="min-h-8"
      />
      {(reference.quantity || reference.note) && (
        <span className="px-2 text-[11px] leading-tight text-slate-500 dark:text-slate-400">
          {reference.quantity && <span>x{reference.quantity}</span>}
          {reference.quantity && reference.note && <span> · </span>}
          {reference.note && <span>{reference.note}</span>}
        </span>
      )}
    </div>
  );
}

function referenceKey(reference: EntryReference) {
  return [
    reference.entryID,
    reference.role ?? '',
    reference.quantity ?? '',
    reference.note ?? '',
  ].join(':');
}

/* ---------------------- RelatedEntryLink ----------------------- */

function RelatedEntryLink({ id, repository }: { id: string; repository: ContentRepository }) {
  const related = repository.entry(id);
  const to = related ? `/topic/${related.topicID}#${related.id}` : '#';

  return <EntryReferenceChip entry={related} fallbackID={id} to={to} />;
}
